import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import {
    DO_NOTHING,
    FEATURE_GENERIC_TASKS,
    FEATURE_ONBOARDING_TASKS,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    PersonRolesDto,
    PersonService,
    PersonsOrganisationDto,
    PreferencesService,
    TaskCategory,
    UserService,
    WorkTracker,
    hasAnyPermission,
    hasFeatureFlag,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig,
    TableOrganisationPersonnelSelectionService
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe, FormatPersonPipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import {
    applyPreferences,
    findDefaultColumnSelection,
    updateDateRange
} from 'libs/flyfreely-table/src/lib/helpers';
import {
    GeneralTask,
    TaskStatus
} from 'libs/flyfreely/src/lib/services/generalTasks.service';
import { GeneralTasksDialoguesService } from 'libs/outstanding-tasks/src/lib/widgets/general-task/general-tasks-dialogues.service';
import { EMPTY, Subject, combineLatest } from 'rxjs';
import { catchError, startWith, takeUntil } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../outstanding-tasks-view.service';
import {
    GeneralTasksDataService,
    formatGeneralTasksPriority,
    formatGeneralTasksStatuses
} from './general-tasks-data.service';

export type TaskWidgetMode = 'GENERAL' | 'ONBOARDING';

interface TaskWidgetConfig {
    mode: TaskWidgetMode;
    title: string;
    icon: string;
    category: TaskCategory;
    preferencesKey: string;
    buttonText: string;
    systemAdminOnly: boolean;
}

const TASK_WIDGET_CONFIGS: Record<TaskWidgetMode, TaskWidgetConfig> = {
    GENERAL: {
        mode: 'GENERAL',
        title: 'General Tasks',
        icon: 'fa-list-check',
        category: TaskCategory.GENERAL,
        preferencesKey: 'generalTasksWidgetTable',
        buttonText: '+ New Task',
        systemAdminOnly: false
    },
    ONBOARDING: {
        mode: 'ONBOARDING',
        title: 'Onboarding Tasks',
        icon: 'fa-clipboard-list',
        category: TaskCategory.ONBOARDING,
        preferencesKey: 'onboardingTasksWidgetTable',
        buttonText: '+ New Onboarding Task',
        systemAdminOnly: true
    }
};

const priorityOptions = Object.entries(formatGeneralTasksPriority).map(
    ([value, name]) => ({ name, value })
);
const statusOptions = Object.entries(formatGeneralTasksStatuses).map(
    ([value, name]) => ({ name, value })
);

class AssigneeService extends TableOrganisationPersonnelSelectionService {}
class ReporterService extends TableOrganisationPersonnelSelectionService {}

@Component({
    selector: 'general-tasks-widget',
    templateUrl: './general-tasks-widget.component.html',
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()],
    providers: [
        GeneralTasksDialoguesService,
        GeneralTasksDataService,
        {
            provide: AssigneeService,
            useClass: TableOrganisationPersonnelSelectionService
        },
        {
            provide: ReporterService,
            useClass: TableOrganisationPersonnelSelectionService
        }
    ],
    styleUrls: ['./general-tasks-widget.component.scss']
})
export class GeneralTasksWidgetComponent implements OnInit, OnDestroy {
    @Input() mode: TaskWidgetMode = 'GENERAL';

    organisation: PersonsOrganisationDto;
    canAddTask: boolean;
    isSystemAdmin: boolean = false;
    showBody = true;

    generalTasks: GeneralTask[];
    config: TaskWidgetConfig;

    availableColumns: TableColumn[];
    defaultAvailableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    columnSorting: ColumnSortPreferences;
    tableSearch: any;

    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    protected readonly formatPriority = formatGeneralTasksPriority;
    protected readonly formatStatuses = formatGeneralTasksStatuses;

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private generalTasksDataService: GeneralTasksDataService,
        private formatDatePipe: FormatDatePipe,
        private generalTasksDialoguesService: GeneralTasksDialoguesService,
        private preferencesService: PreferencesService,
        private personService: PersonService,
        private formatPerson: FormatPersonPipe,
        private changeDetector: ChangeDetectorRef,
        private userService: UserService,
        private assigneeSelectService: AssigneeService,
        private reporterSelectService: ReporterService
    ) {
        combineLatest([
            this.workTracker.observable.pipe(startWith(false)),
            this.generalTasksDataService.working$.pipe(startWith(false))
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                ([working, serviceWorking]) =>
                    (this.working = working || serviceWorking)
            );

        this.availableColumns = [
            {
                value: 'summary',
                name: 'Title',
                searchable: true,
                selectable: false,
                sortable: false,
                defaultSelection: true
            },
            {
                value: 'assignee',
                name: 'Assignee',
                searchable: 'async-selection',
                selectable: false,
                defaultSelection: true,
                asyncSelectService: this.assigneeSelectService
            },
            {
                value: 'reporter',
                name: 'Reporter',
                searchable: 'async-selection',
                selectable: false,
                defaultSelection: true,
                asyncSelectService: this.reporterSelectService
            },
            {
                value: 'dueDate',
                name: 'Due Date',
                searchable: 'daterange',
                selectable: false,
                defaultSelection: true,
                formatterFunction: t => this.formatDatePipe.transform(t)
            },
            {
                value: 'priority',
                name: 'Priority',
                searchable: 'selection',
                selectable: false,
                defaultSelection: true,
                searchOptions: priorityOptions
            },
            {
                value: 'status',
                name: 'Status',
                searchable: 'selection',
                selectable: false,
                defaultSelection: true,
                searchOptions: statusOptions
            }
        ];
        this.defaultAvailableColumns = this.availableColumns;
    }

    ngOnInit() {
        this.config = TASK_WIDGET_CONFIGS[this.mode];

        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.generalTasks = [];
                this.assigneeSelectService.organisationId =
                    this.organisation.id;
                this.reporterSelectService.organisationId =
                    this.organisation.id;
                this.loadPreferences();
                this.refreshPermissions();
            });

        combineLatest([
            this.generalTasksDataService.generalTasks$,
            this.generalTasksDataService.currentPage$,
            this.generalTasksDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([tasks, currentPage, totalItems]) => {
                this.generalTasks = tasks;
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });

        this.generalTasksDataService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshTasks();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    get widgetTitle(): string {
        return this.config.title;
    }

    get widgetIcon(): string {
        return this.config.icon;
    }

    get addButtonText(): string {
        return this.config.buttonText;
    }

    get showAddButton(): boolean {
        if (!this.canAddTask) return false;
        if (this.config.systemAdminOnly) {
            return this.isSystemAdmin;
        }
        return true;
    }

    onTableSearch(search: any) {
        this.tableSearch = search;
        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: search
        };
        if (this.tableConfig != null) {
            this.tableConfig.currentPage = 0;
        }
        this.updateUserPreferences();
        this.refreshTasks();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    // Reset the user preferences on organisation change
    private resetUserPreferences() {
        this.userPreferences = null;
        this.availableColumns = this.defaultAvailableColumns;
        this.selectedColumns = findDefaultColumnSelection(
            this.availableColumns
        );
        this.columnSorting = null;
        const updatedConfig = {
            ...this.tableConfig,
            currentPage: 0,
            limit: 3
        };
        this.tableConfig = updatedConfig;
        this.showBody = true;
        this.tableSearch = null;
    }

    private loadPreferences() {
        this.resetUserPreferences();
        if (!this.organisation) {
            return;
        }

        this.tableConfig = {
            timeZone: this.organisation.timeZone,
            serverPagination: true,
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                this.config.preferencesKey,
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.selectedColumns = findDefaultColumnSelection(
                        this.availableColumns
                    );
                    return EMPTY;
                })
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
                this.changeDetector.detectChanges();
                this.refreshTasks();
            })
            .add(this.workTracker.createTracker());
    }

    private setupTableColumns(personList: PersonRolesDto[]) {
        DO_NOTHING;
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private refreshPermissions() {
        this.isSystemAdmin =
            this.userService.getCurrentUser().type === 'SYSTEM_ADMIN';
        this.canAddTask = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.TASK_ADD
        );
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        const updatedConfig = {
            ...this.tableConfig,
            limit: this.userPreferences.itemLimit
        };
        this.tableConfig = updatedConfig;
        this.showBody = this.userPreferences.showBody ?? true;
        this.tableSearch = updateDateRange(
            this.userPreferences?.tableSearch ?? {},
            'dueDate'
        );
    }

    viewElement(task: GeneralTask) {
        if (task.status === TaskStatus.NEW) {
            this.generalTasksDialoguesService.showTaskEditDialogue(
                task,
                this.organisation,
                this.generalTasksDataService
            );
        } else {
            this.generalTasksDialoguesService.showTaskDetailsDialogue(
                task,
                this.organisation,
                this.generalTasksDataService
            );
        }
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshTasks();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshTasks();
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    refreshTasks() {
        const parsedTableSearch = this.tableSearch
            ? {
                  reporterId: this.tableSearch.reporter,
                  assigneeId: this.tableSearch.assignee,
                  text: this.tableSearch.summary,
                  status: this.tableSearch.status,
                  priority: this.tableSearch.priority,
                  ...(this.tableSearch.dueDate != null
                      ? {
                            dueDate: {
                                start: toTimestamp(this.tableSearch.dueDate[0]),
                                end: toTimestamp(this.tableSearch.dueDate[1])
                            }
                        }
                      : {})
              }
            : {};
        this.generalTasksDataService.updateTaskTable({
            organisationId: this.organisation.id,
            page: this.tableConfig.currentPage ?? 0,
            limit: this.tableConfig.limit ?? 3,
            sorting: this.columnSorting,
            category: this.config.category,
            ...parsedTableSearch
        });
    }

    onAddNewTask() {
        this.generalTasksDialoguesService.showAddTaskDialogue(
            this.organisation,
            this.generalTasksDataService,
            this.config.category
        );
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                this.config.preferencesKey,
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }
}

export const generalTasksWidgetRegistration: WidgetRegistrationWithSection = {
    widgetIdentifier: 'generalTasksWidget',
    section: 'general',
    component: GeneralTasksWidgetComponent,
    isAvailable: organisation =>
        hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW) &&
        hasFeatureFlag(organisation, FEATURE_GENERIC_TASKS),
    componentInputs: { mode: 'GENERAL' },
    requiredPermissions: [PersonsOrganisationDto.Permissions.ORGANISATION_VIEW]
};

export const onboardingTasksWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: 'onboardingTasksWidget',
        section: 'onboarding',
        component: GeneralTasksWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW) &&
            hasFeatureFlag(organisation, FEATURE_ONBOARDING_TASKS),
        componentInputs: { mode: 'ONBOARDING' },
        requiredPermissions: [PersonsOrganisationDto.Permissions.ORGANISATION_VIEW]
    };
